import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { AppLayout } from "@/components/layout/app-layout";
import { ContentLayout } from "@/components/layout/content-layout";
import { DiscoveryLayout } from "@/components/layout/discovery-layout";
import { EntityAssetsLayout } from "@/components/layout/entity-assets-layout";
import { ModerationLayout } from "@/components/layout/moderation-layout";
import { RevenueLayout } from "@/components/layout/revenue-layout";
import { UsersAccessLayout } from "@/components/layout/users-access-layout";
import { ProtectedRoute } from "@/features/auth/components/protected-route";
import { RoleGate } from "@/features/auth/components/role-gate";
import { AdminsPage } from "@/pages/admins/admins-page";
import { AdvertisersPage } from "@/pages/advertisers/advertisers-page";
import { AnalyticsOverviewPage } from "@/pages/analytics/analytics-overview";
import { CampaignListPage } from "@/pages/campaigns/campaign-list";
import { ContentDetailPage } from "@/pages/content/content-detail";
import { ContentListPage } from "@/pages/content/content-list";
import { NewWritersPage } from "@/pages/content/new-writers";
import { TrustedSitesPage } from "@/pages/content/trusted-sites";
import { DashboardHomePage } from "@/pages/dashboard/dashboard-home";
import { EntityCorrectionsPage } from "@/pages/entities/entity-corrections";
import { EntityEditorPage } from "@/pages/entities/entity-editor";
import { QueryPreservingRedirect } from "@/pages/entities/legacy-redirect";
import { LoginPage } from "@/pages/login/login-page";
import { MachineTagQueuePage } from "@/pages/machine-tags/machine-tag-queue";
import { AuthorizedPublishersPage } from "@/pages/moderation/authorized-publishers";
import { JudgeLogPage } from "@/pages/moderation/judge-log";
import { ModerationProbePage } from "@/pages/moderation/moderation-probe";
import { ModerationQueuePage } from "@/pages/moderation/moderation-queue";
import { PageEditorPage } from "@/pages/pages-manager/page-editor";
import { PagesManagerPage } from "@/pages/pages-manager/pages-manager";
import { PromoCodesPage } from "@/pages/revenue/promo-codes";
import { RevenueOverviewPage } from "@/pages/revenue/revenue-overview";
import { EditorialOverridesPage } from "@/pages/search/editorial-overrides";
import { AuditLogsPage } from "@/pages/system/audit-logs";
import { ForbiddenPage } from "@/pages/system/forbidden";
import { NotFoundPage } from "@/pages/system/not-found";
import { TaxonomyPage } from "@/pages/taxonomy/taxonomy-page";
import { UserDetailPage } from "@/pages/users/user-detail";
import { UsersListPage } from "@/pages/users/users-list";

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route index element={<DashboardHomePage />} />

            {/*
             * The four sections with more than one child route (Moderation,
             * Discovery, Content, Users & Access) share a layout that renders
             * a tab bar across their children instead of giving each child
             * its own sidebar row - see sidebar.tsx and section-tabs-layout.tsx.
             * Every child route below keeps its exact pre-existing path and
             * RoleGate wrapping; only the nesting changed.
             */}
            <Route path="moderation" element={<ModerationLayout />}>
              <Route index element={<Navigate to="queue" replace />} />
              <Route
                path="queue"
                element={
                  <RoleGate minRole="admin">
                    <ModerationQueuePage />
                  </RoleGate>
                }
              />
              <Route
                path="authorized-publishers"
                element={
                  <RoleGate minRole="admin">
                    <AuthorizedPublishersPage />
                  </RoleGate>
                }
              />
              <Route
                path="probe"
                element={
                  <RoleGate minRole="admin">
                    <ModerationProbePage />
                  </RoleGate>
                }
              />
              <Route
                path="judge"
                element={
                  <RoleGate minRole="admin">
                    <JudgeLogPage />
                  </RoleGate>
                }
              />
            </Route>

            <Route element={<DiscoveryLayout />}>
              <Route
                path="search/editorial-overrides"
                element={
                  <RoleGate minRole="admin">
                    <EditorialOverridesPage />
                  </RoleGate>
                }
              />
              <Route
                path="taxonomy"
                element={
                  <RoleGate minRole="admin">
                    <TaxonomyPage />
                  </RoleGate>
                }
              />
              {/* The drain surface for machine-proposed entity tags. It sits in
                  Discovery rather than Moderation because an accepted tag is
                  discovery metadata (it folds into the index and renders as a
                  chip beside the author's own subject refs), not a safety
                  verdict. */}
              <Route
                path="machine-tags"
                element={
                  <RoleGate minRole="admin">
                    <MachineTagQueuePage />
                  </RoleGate>
                }
              />
              {/*
               * Entity assets took its own subtabs (#85): the page stacked the
               * picker, the overrides list and the art list on one screen, and
               * the owner asked for it split. Same nesting pattern as Revenue
               * below - a route with children of its own, wrapped in the same
               * SectionTabsLayout - just one level deeper than Discovery's own
               * tab bar.
               *
               * TWO TABS, FOUR PATHS. The art list has since folded into
               * Corrections, so two of these are redirects rather than pages.
               * Both keep their query string: `?id=` is the cross-tab load
               * contract and a bare redirect would land an old link on an empty
               * picker. See legacy-redirect.tsx.
               */}
              <Route path="entities/assets" element={<EntityAssetsLayout />}>
                <Route index element={<Navigate to="entity" replace />} />
                <Route
                  path="entity"
                  element={
                    <RoleGate minRole="admin">
                      <EntityEditorPage />
                    </RoleGate>
                  }
                />
                {/* The segment was "editor" until the tab was renamed off the
                    collision with @breakingthelines/editor. */}
                <Route
                  path="editor"
                  element={<QueryPreservingRedirect to="/entities/assets/entity" />}
                />
                <Route
                  path="overrides"
                  element={
                    <RoleGate minRole="admin">
                      <EntityCorrectionsPage />
                    </RoleGate>
                  }
                />
                {/* Artwork was a tab of its own until its rows moved in beside
                    the text corrections. */}
                <Route
                  path="artwork"
                  element={<QueryPreservingRedirect to="/entities/assets/overrides" />}
                />
              </Route>
            </Route>

            <Route element={<ContentLayout />}>
              <Route
                path="content"
                element={
                  <RoleGate minRole="admin">
                    <ContentListPage />
                  </RoleGate>
                }
              />
              <Route
                path="pages"
                element={
                  <RoleGate minRole="admin">
                    <PagesManagerPage />
                  </RoleGate>
                }
              />
              <Route
                path="pages/:pageSlug"
                element={
                  <RoleGate minRole="admin">
                    <PageEditorPage />
                  </RoleGate>
                }
              />
              <Route
                path="trusted-sites"
                element={
                  <RoleGate minRole="admin">
                    <TrustedSitesPage />
                  </RoleGate>
                }
              />
              <Route
                path="new-writers"
                element={
                  <RoleGate minRole="admin">
                    <NewWritersPage />
                  </RoleGate>
                }
              />
            </Route>
            {/* Content detail is a drill-down, not a tab of Content vs Pages - it
                stays outside ContentLayout so it renders without the tab bar. */}
            <Route
              path="content/:contentId"
              element={
                <RoleGate minRole="admin">
                  <ContentDetailPage />
                </RoleGate>
              }
            />

            <Route element={<UsersAccessLayout />}>
              <Route
                path="users"
                element={
                  <RoleGate minRole="admin">
                    <UsersListPage />
                  </RoleGate>
                }
              />
              <Route
                path="admins"
                element={
                  <RoleGate minRole="admin">
                    <AdminsPage />
                  </RoleGate>
                }
              />
              <Route
                path="advertisers"
                element={
                  <RoleGate minRole="admin">
                    <AdvertisersPage />
                  </RoleGate>
                }
              />
              <Route
                path="audit"
                element={
                  <RoleGate minRole="admin">
                    <AuditLogsPage />
                  </RoleGate>
                }
              />
            </Route>
            {/*
             * The Scholarships manage view moved onto the Users page as a
             * tab (see users-list.tsx) so it shares the Users page's chrome
             * instead of being a separate page. This keeps the old URL
             * working as a deep link into that tab, for anyone with it
             * bookmarked or linked elsewhere.
             */}
            <Route
              path="users/scholarships"
              element={<Navigate to="/users?tab=scholarships" replace />}
            />
            {/* User detail is a drill-down, not a tab of Users & Access - it
                stays outside UsersAccessLayout so it renders without the tab bar. */}
            <Route
              path="users/:id"
              element={
                <RoleGate minRole="admin">
                  <UserDetailPage />
                </RoleGate>
              }
            />

            <Route
              path="campaigns"
              element={
                <RoleGate minRole="admin">
                  <CampaignListPage />
                </RoleGate>
              }
            />
            {/*
             * Campaign creation and editing are not built - the campaigns
             * page itself is a coming-soon shell (see campaign-list.tsx).
             * Both routes redirect to it rather than rendering a dead form,
             * the same pattern used for the old scholarships URL below.
             */}
            <Route path="campaigns/new" element={<Navigate to="/campaigns" replace />} />
            <Route
              path="campaigns/:campaignId/edit"
              element={<Navigate to="/campaigns" replace />}
            />
            {/* Revenue is its own section, not a tab of Analytics: it reads
                subscription-service's own ledger, while Analytics reads
                platform reader events and publishing rows. It has a second child now,
                so it takes the shared tab layout the other multi-view sections
                use - see revenue-layout.tsx. The overview keeps /revenue as an
                index route rather than redirecting to a child, so the URL PR
                #66 shipped stays the page it was. */}
            <Route path="revenue" element={<RevenueLayout />}>
              <Route
                index
                element={
                  <RoleGate minRole="admin">
                    <RevenueOverviewPage />
                  </RoleGate>
                }
              />
              <Route
                path="promo-codes"
                element={
                  <RoleGate minRole="admin">
                    <PromoCodesPage />
                  </RoleGate>
                }
              />
            </Route>
            <Route
              path="analytics/overview"
              element={
                <RoleGate minRole="admin">
                  <AnalyticsOverviewPage />
                </RoleGate>
              }
            />
          </Route>
        </Route>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/403" element={<ForbiddenPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}
